import localforage from "localforage";

export interface OfflineProject {
    offlineId: string;
    payload: any;
    timestamp: number;
    synced: boolean;
}

export interface OfflineInspection {
    offlineId: string; // Links to the OfflineProject
    payload: any;
    timestamp: number;
    synced: boolean;
}

export interface OfflinePhoto {
    offlineId: string;
    taskId: string;
    projectId: string;
    label: string;
    file: Blob;
    fileName: string;
    note?: string;
    timestamp: number;
}

localforage.config({
    name: "colorado-roofing",
    storeName: "offline_queue", // Should be alphanumeric, with underscores.
});

const STORE_KEYS = {
    PROJECTS: "offline_projects",
    INSPECTIONS: "offline_inspections",
    PROJECT_MAPPINGS: "offline_project_mappings",
    PHOTOS: "offline_photos",
    CACHED_USERS: "cached_org_users",
    SYNC_FAILURES: "offline_sync_failures",
};

export type OfflineSyncFailure = {
    kind: "project" | "inspection" | "photo";
    id: string;
    message: string;
    at: number;
};

export const offlineStore = {
    // Projects
    async saveProject(project: OfflineProject) {
        const projects = await this.getProjects();
        projects.push(project);
        await localforage.setItem(STORE_KEYS.PROJECTS, projects);
    },
    async getProjects(): Promise<OfflineProject[]> {
        const projects = await localforage.getItem<OfflineProject[]>(STORE_KEYS.PROJECTS);
        return projects || [];
    },
    async removeProject(offlineId: string) {
        const projects = await this.getProjects();
        await localforage.setItem(
            STORE_KEYS.PROJECTS,
            projects.filter((p) => p.offlineId !== offlineId)
        );
    },

    // Inspections
    async saveInspection(inspection: OfflineInspection) {
        const inspections = await this.getInspections();
        const existingIndex = inspections.findIndex(i => i.offlineId === inspection.offlineId);
        
        if (existingIndex >= 0) {
            inspections[existingIndex] = inspection;
        } else {
            inspections.push(inspection);
        }
        await localforage.setItem(STORE_KEYS.INSPECTIONS, inspections);
    },
    async getInspections(): Promise<OfflineInspection[]> {
        const inspections = await localforage.getItem<OfflineInspection[]>(STORE_KEYS.INSPECTIONS);
        return inspections || [];
    },
    async removeInspection(offlineId: string) {
        const inspections = await this.getInspections();
        await localforage.setItem(
            STORE_KEYS.INSPECTIONS,
            inspections.filter((i) => i.offlineId !== offlineId)
        );
    },

    // Photos
    async saveOfflinePhoto(photo: OfflinePhoto) {
        const photos = await this.getOfflinePhotos();
        const existingIndex = photos.findIndex((p) => p.offlineId === photo.offlineId);
        
        if (existingIndex >= 0) {
            photos[existingIndex] = photo;
        } else {
            photos.push(photo);
        }
        await localforage.setItem(STORE_KEYS.PHOTOS, photos);
    },
    async getOfflinePhotos(): Promise<OfflinePhoto[]> {
        const photos = await localforage.getItem<OfflinePhoto[]>(STORE_KEYS.PHOTOS);
        return photos || [];
    },
    async removeOfflinePhoto(offlineId: string) {
        const photos = await this.getOfflinePhotos();
        await localforage.setItem(
            STORE_KEYS.PHOTOS,
            photos.filter((p) => p.offlineId !== offlineId)
        );
    },

    // Clear all
    async clearAll() {
        await localforage.clear();
    },

    // ID Mappings
    async saveProjectMapping(offlineId: string, realId: string) {
        const mappings = await localforage.getItem<Record<string, string>>(STORE_KEYS.PROJECT_MAPPINGS) || {};
        mappings[offlineId] = realId;
        await localforage.setItem(STORE_KEYS.PROJECT_MAPPINGS, mappings);
    },
    async getProjectMapping(offlineId: string): Promise<string | null> {
        const mappings = await localforage.getItem<Record<string, string>>(STORE_KEYS.PROJECT_MAPPINGS);
        return mappings?.[offlineId] || null;
    },

    // Cached org users (Quick Create salesman dropdown)
    async saveCachedUsers(organizationId: string, users: unknown[]) {
        const all =
            (await localforage.getItem<Record<string, unknown[]>>(
                STORE_KEYS.CACHED_USERS,
            )) || {};
        all[organizationId] = users;
        await localforage.setItem(STORE_KEYS.CACHED_USERS, all);
    },
    async getCachedUsers(organizationId: string): Promise<unknown[]> {
        const all =
            (await localforage.getItem<Record<string, unknown[]>>(
                STORE_KEYS.CACHED_USERS,
            )) || {};
        return all[organizationId] || [];
    },

    // Sync failure log (last attempt)
    async saveSyncFailures(failures: OfflineSyncFailure[]) {
        await localforage.setItem(STORE_KEYS.SYNC_FAILURES, failures);
    },
    async getSyncFailures(): Promise<OfflineSyncFailure[]> {
        return (
            (await localforage.getItem<OfflineSyncFailure[]>(
                STORE_KEYS.SYNC_FAILURES,
            )) || []
        );
    },

    async getPendingCounts(): Promise<{
        projects: number;
        inspections: number;
        photos: number;
    }> {
        const [projects, inspections, photos] = await Promise.all([
            this.getProjects(),
            this.getInspections(),
            this.getOfflinePhotos(),
        ]);
        return {
            projects: projects.length,
            inspections: inspections.length,
            photos: photos.length,
        };
    },
};
